<template>
    <div class="" v-if="!subscriptionIsGroupManaged">
        <Tooltip width-px="300">
            <template #trigger>
                <div class="text-gray-600">Self-managed</div>
            </template>
            <template #content>
                This student has a self-managed subscription. You will not be
                able to extend them with a group membership until their
                subscription has expired.
            </template>
        </Tooltip>
    </div>

    <new-modal
        v-else
        title="Extend Membership"
        size="2xl"
        ref="extendStudentModal"
    >
        <template #trigger>
            <span class="link"> + Extend Membership </span>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <div class="text-gray-750">
                    Extending a membership will add the selected number of days
                    to the student’s current membership.
                </div>
                <hr/>
                <form-errors form-name="extend_memberships_form"/>
                <div class="flex gap-4">
                    <div class="">
                        <user-avatar-round :user="student" size="60"/>
                    </div>
                    <div class="flex flex-col gap-2">
                        <div class="font-bold">{{ student.full_name }}</div>
                        <div v-if="student.subscription" class="rounded bg-gray-350 px-2 py-1 text-[13px]">
                            {{ student.subscription.days_left }} days remaining
                        </div>
                    </div>
                </div>
                <div class="">
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Type</th>
                            <th># Available</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <template
                            v-for="(membershipPlan, index) in $page.props
                                    .groupMembershipPlans"
                        >
                            <tr v-if="membershipPlan.available > 0">
                                <td>{{ membershipPlan.plan_title }}</td>
                                <td>
                                    <div class="flex items-center gap-2">
                                        <svg
                                            width="7"
                                            height="7"
                                            viewBox="0 0 7 7"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle
                                                cx="3.5"
                                                cy="3.5"
                                                r="3.5"
                                                fill="#34A853"
                                            />
                                        </svg>
                                        <span>{{
                                                membershipPlan.available
                                            }}</span>
                                        <span>memberships</span>
                                    </div>
                                </td>
                                <td>
                                    <div
                                        class="w-auto rounded border border-gray-450"
                                    >
                                        <div class="flex">
                                            <input
                                                class="flex w-12 flex-grow appearance-none items-center border-none px-4"
                                                type="text"
                                                step="1"
                                                min="0"
                                                @keydown="onlyAllowNumbers"
                                                v-model="
                                                        form.membershipsToApply[
                                                            index
                                                        ].qty
                                                    "
                                            />
                                            <div
                                                class="flex w-8 flex-none flex-col text-center text-[15px] font-semibold"
                                            >
                                                <div
                                                    @click="
                                                            incrementMembershipPlan(
                                                                index
                                                            )
                                                        "
                                                    class="flex h-1/2 items-center justify-center rounded-tr border-b border-gray-450 bg-gray-350 p-2 hover:cursor-pointer hover:bg-gray-350"
                                                >
                                                    <svg
                                                        width="10"
                                                        height="10"
                                                        viewBox="0 0 10 10"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M5 0.916016V9.08268"
                                                            stroke="#777777"
                                                            stroke-width="1.16667"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                        <path
                                                            d="M0.914062 5H9.08073"
                                                            stroke="#777777"
                                                            stroke-width="1.16667"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                                <div
                                                    @click="
                                                            decrementMembershipPlan(
                                                                index
                                                            )
                                                        "
                                                    class="flex h-1/2 h-1/2 items-center justify-center rounded-br bg-gray-350 p-2 hover:cursor-pointer hover:bg-gray-350"
                                                >
                                                    <svg
                                                        width="10"
                                                        height="2"
                                                        viewBox="0 0 10 2"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M0.914062 1H9.08073"
                                                            stroke="#A6A6A6"
                                                            stroke-width="1.16667"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
        <template #controls>
            <div class="flex w-full justify-end gap-4">
                <div class="btn btn-outline" @click="cancel">Cancel</div>
                <div
                    v-if="daysToExtend > 0"
                    class="btn btn-primary"
                    @click="submit"
                >
                    Extend Membership (+{{ daysToExtend }} days)
                </div>
                <div v-else class="btn btn-primary opacity-50">
                    Extend Membership
                </div>
            </div>
        </template>
    </new-modal>
</template>

<script>
import UserAvatarRound from "@/Components/UserAvatarRound";
import NewModal from "@/Components/NewModal";
import FormErrors from "@/Components/FormErrors";
import Tooltip from "@/Components/Tooltip";

export default {
    name: "GroupStudentExtendMembershipModal",
    components: {FormErrors, UserAvatarRound, NewModal, Tooltip},
    props: {
        student: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            daysToExtend: 0,
            form: this.$inertia.form({
                membershipsToApply: [],
            }),
        };
    },
    methods: {
        cancel() {
            this.$refs.extendStudentModal.closeModal();
        },
        prepareMembershipsToApplyArray() {
            let vm = this;

            vm.form.reset();

            vm.$page.props.groupMembershipPlans.forEach(function (plan, index) {
                vm.form.membershipsToApply[index] = {
                    planId: plan.plan_id,
                    qty: 0,
                    length: parseInt(plan.plan_length),
                };
            });
        },
        updateMemberhipExtension() {
            let vm = this;
            vm.daysToExtend = 0;

            vm.form.membershipsToApply.forEach(function (membership) {
                if (membership.qty > 0) {
                    vm.daysToExtend =
                        vm.daysToExtend + membership.length * membership.qty;
                }
            });
        },
        incrementMembershipPlan(index) {
            this.form.membershipsToApply[index].qty++;
        },
        decrementMembershipPlan(index) {
            if (this.form.membershipsToApply[index].qty > 0) {
                this.form.membershipsToApply[index].qty--;
            }
        },
        onlyAllowNumbers(event) {
            let keyCode = event.keyCode ? event.keyCode : event.which;
            if (
                (keyCode < 48 || keyCode > 57) &&
                (keyCode < 96 || keyCode > 105) &&
                keyCode !== 9 && // tab
                keyCode !== 8 && // delete
                keyCode !== 46 // backspace
            ) {
                event.preventDefault();
            }
        },
        submit() {
            let vm = this;
            vm.$inertia.post(
                route("group.student.extend-membership", {
                    user: vm.student.id,
                }),
                vm.form,
                {
                    onSuccess() {
                        vm.prepareMembershipsToApplyArray();
                        vm.$refs.extendStudentModal.closeModal();
                    },
                    onError(errors) {
                        console.log(errors);
                    },
                }
            );
        },
    },
    created() {
        this.prepareMembershipsToApplyArray();
    },
    computed: {
        subscriptionIsGroupManaged() {

            if (!this.student.subscription) {
                return true;
            }

            return this.student.subscription.plan.type === 1;
        },
    },
    watch: {
        "form.membershipsToApply": {
            handler: _.debounce(function (val) {
                this.updateMemberhipExtension();
            }, 100),
            deep: true,
        },
    },
};
</script>
